import { useEffect, useState } from 'react';
import { Studio } from './components/Studio';
import { ModeToggle } from './components/ModeToggle';
import { HowToUse } from './components/HowToUse';
import { useAppState } from './hooks/useAppState';
import { DEFAULT_MODE, type ModePref, resolveTheme } from './themes';
import { ExampleToolbarLauncher } from './components/examples/ExampleToolbarLauncher';
import { LinksV1_Footer } from './components/links/LinksV1_Footer';

const STORAGE_KEY = 'fcp-mode';

const readStoredMode = (): ModePref => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === 'auto' || raw === 'light' || raw === 'dark' ? raw : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
};

const prefersDarkQuery = '(prefers-color-scheme: dark)';

const readSystemDark = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(prefersDarkQuery).matches;
};

export default function App() {
  const app = useAppState();
  const [mode, setMode] = useState<ModePref>(() => readStoredMode());
  const [systemDark, setSystemDark] = useState<boolean>(() => readSystemDark());
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* localStorage unavailable */
    }
  }, [mode]);

  useEffect(() => {
    const mql = window.matchMedia(prefersDarkQuery);
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const theme = resolveTheme(mode, systemDark);

  return (
    <div className={`theme-root ${theme.className} theme-mode-${theme.mode}`}>
      <div className="studio-toolbar">
        <ExampleToolbarLauncher app={app} />
        <button
          type="button"
          className="studio-help"
          onClick={() => setShowHelp((v) => !v)}
          aria-pressed={showHelp}
        >
          {showHelp ? '✕ Close guide' : '? Guide'}
        </button>
        <ModeToggle mode={mode} onChange={setMode} />
      </div>

      <Studio app={app} />

      <LinksV1_Footer />

      {showHelp && (
        <div className="studio-help-overlay" onClick={() => setShowHelp(false)}>
          <div className="studio-help-card" onClick={(e) => e.stopPropagation()}>
            <HowToUse />
          </div>
        </div>
      )}
    </div>
  );
}
